/**
 * BFCM 2025 - Cart Announcement + Auto-Add Product Promotion
 * @author Aleksey Korzun <a.korzun@flatbushventures.com>
 */

(function() {
  'use strict';

  const IS_DEBUG = false;

  /**
   * Debug logger - outputs to console when IS_DEBUG is true
   * @param {string} message - Message to log
   */
  function debug_log(message) {
    if (!IS_DEBUG) return;
    console.log('[BFCM] ' + message);
  }

  if (!window.themeSettings) {
    return;
  }

  const SETTINGS = {
    showCartAnnouncement: window.themeSettings.showCartAnnouncement || false,
    announcementThreshold: (window.themeSettings.cartAnnouncementThreshold || 0) * 100,
    announcementBackgroundColor: window.themeSettings.cartAnnouncementBackgroundColor || null,
    announcementBorderColor: window.themeSettings.cartAnnouncementBorderColor || null,
    announcementTextColor: window.themeSettings.cartAnnouncementTextColor || null,
    announcementText: window.themeSettings.cartAnnouncementText || null,
    autoAddThreshold: (window.themeSettings.cartAutoAddThreshold || 0) * 100,
    autoAddProductId: window.themeSettings.cartAutoAddProductId || null,
    autoAddProductVariantId: window.themeSettings.cartAutoAddProductVariantId || null,
    hasAutoAddProduct: window.themeSettings.cartAutoAddProductId && window.themeSettings.cartAutoAddProductVariantId
  };

  const hasAnnouncement = SETTINGS.showCartAnnouncement && SETTINGS.announcementText;
  if (!hasAnnouncement && !SETTINGS.hasAutoAddProduct) {
    debug_log('Both features disabled - exiting');
    return;
  }

  /**
   * Injects CSS styles for cart announcement
   */
  function injectAnnouncementStyles() {
    if (document.getElementById('CartAnnouncementStyles')) {
      return;
    }

    const styleTag = document.createElement('style');
    styleTag.id = 'CartAnnouncementStyles';
    styleTag.textContent = `
    #CartAnnouncement {
      padding: 18px 20px;
      margin: 0 0 1.5rem;
      text-align: center;
      background: ${SETTINGS.announcementBackgroundColor};
      border: 3px solid ${SETTINGS.announcementBorderColor};
      font-size: 13px;
      line-height: 1.5;
      letter-spacing: 0.3px;
      color: ${SETTINGS.announcementTextColor};
      text-transform: uppercase;
    }

    #CartAnnouncement p {
      margin: 0;
    }

    #CartAnnouncement strong,
    #CartAnnouncement a {
      color: ${SETTINGS.announcementTextColor};
      font-weight: 600;
    }

    #CartAnnouncement a {
      text-decoration: underline;
    }

    #CartAnnouncement a:hover {
      opacity: 0.8;
    }
  `;
    
    document.head.appendChild(styleTag);
  }

  /**
   * Hydrates the cart announcement
   * @param {number} cartTotalPrice - Cart total in cents
   */
  function hydrateCartAnnouncement(cartTotalPrice) {
    const cartDrawerHeader = document.querySelector('#CartDrawer .drawer__header');
    if (!cartDrawerHeader) {
      debug_log('Cart drawer header element not found');
      return;
    }

    let cartAnnouncement = document.getElementById('CartAnnouncement');
    if (!cartAnnouncement) {
      debug_log('Creating announcement element');
      cartAnnouncement = document.createElement('div');
      cartAnnouncement.id = 'CartAnnouncement';
      cartDrawerHeader.insertAdjacentElement('afterend', cartAnnouncement);
    }

    const shouldShow = SETTINGS.announcementThreshold === 0 || cartTotalPrice < SETTINGS.announcementThreshold;
    if (shouldShow) {
      cartAnnouncement.style.display = "block";
      cartAnnouncement.innerHTML = SETTINGS.announcementText;
      debug_log('Showing announcement - threshold not set or below threshold');
    } else {
      cartAnnouncement.style.display = "none";
      debug_log('Hiding announcement - threshold met or exceeded');
    }
  }

  let isProcessing = false;

  /**
   * Processes cart for auto-add product
   * @async
   */
  async function processCartForAutoAdd() {
    if (isProcessing) {
      debug_log('Already processing, skipping...');
      return;
    }

    isProcessing = true;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      if (SETTINGS.showCartAnnouncement && SETTINGS.announcementText) {
        hydrateCartAnnouncement(cart.total_price);
      }

      if (!SETTINGS.hasAutoAddProduct || !SETTINGS.autoAddThreshold) {
        return;
      }

      const isAutoAddProductInCart = cart.items.some(function(item) {
        return item.product_id === SETTINGS.autoAddProductId;
      });

      if (isAutoAddProductInCart) {
        debug_log('Auto-add product already in cart');
        return;
      }

      if (cart.total_price >= SETTINGS.autoAddThreshold) {
        debug_log('Auto-add threshold reached - adding product (ID: ' + SETTINGS.autoAddProductId + ')...');

        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            items: [
              {
                id: SETTINGS.autoAddProductVariantId,
                quantity: 1
              }
            ]
          })
        });

        if (addResponse.ok) {
          debug_log('Auto-add product added successfully');

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-auto-add'
            });
          }
        }
      }
    } catch (error) {
      debug_log('Error: ' + error.message);
    } finally {
      isProcessing = false;
    }
  }

  /**
   * Initialize event listeners
   */
  function initializeEventListeners() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function(event) {
        if (event && event.source !== 'cart-auto-add') {
          processCartForAutoAdd();
        }
      });
      debug_log('Subscribed to cart update events');
    } else {
      debug_log('PUB_SUB_EVENTS not available - cart updates may not trigger');
    }
  }

  // Initialize
  if (SETTINGS.showCartAnnouncement && SETTINGS.announcementText) {
    injectAnnouncementStyles();
  }
  initializeEventListeners();
  processCartForAutoAdd();
})();